import {AttrsSchema, Component} from 'panel';

import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {
  CellClickEvent,
  CellCoordinates,
  CollapseChangeEvent,
  SortChangeEvent,
} from 'common/components/data-table/types';
import {hasRetentionData, RowWithAggregate} from 'common/report/retention/util/parsing';
import {RetentionQuery} from 'common/report/retention/models/retention-query';
import {getEventDisplayName} from 'common/report/retention/util';
import {RetentionDataTableRow} from 'common/report/retention/util/types';
import {propagateEvent} from 'common/util/dom';
import {RetentionPopover, trackPopover} from 'common/report/retention/visualization/popover';
import {RetentionTrackingEvents as Events} from 'common/report/retention/util/constants';

import {template} from './template';
import css from './index.inline.styl';

export interface TablePopoverState {
  open: boolean;
  popover?: RetentionPopover;
  retainedPercent?: string;
  dropOffPercent?: string;
  bgColor?: string;
  textColor?: string;
  rowIdx?: number;
  groupedRowIdx?: number;
  columnIdx?: number;
  absoluteColumnIdx?: number;
  targetCell?: HTMLElement;
}

interface TableState {
  query: RetentionQuery;
  popoverState: TablePopoverState;
}

export interface RetentionTableAttrs {
  'project-id': number;
  'project-timezone': string;
  'resizing-layout': boolean;
}

type RetentionTableJSX = {
  props?: {
    rowData: RowWithAggregate[];
    query: RetentionQuery;
  };
  attrs: RetentionTableAttrs;
  on: {
    sortChange: (ev: SortChangeEvent) => void;
    changeSortColumn: (ev: CustomEvent<{column: string}>) => void;
    changeSortDesc: (ev: CustomEvent<{desc: boolean}>) => void;
    update: (ev: CustomEvent<Partial<RetentionQuery>>) => void;
  };
};

export class RetentionTable extends Component<
  TableState,
  RetentionTableAttrs,
  unknown,
  // not currently attached as a child, this.app === this
  unknown,
  ChartsContextRegistry
> {
  private _results: RowWithAggregate[];

  static get attrsSchema(): AttrsSchema<RetentionTableAttrs> {
    return {
      'project-id': `number`,
      'project-timezone': `string`,
      'resizing-layout': `boolean`,
    };
  }

  get config() {
    return {
      css,
      useShadowDom: true,
      template,
      contexts: [`urlManager` as const],
      defaultState: {
        query: null,
        popoverState: {
          open: false,
        },
      },
      helpers: {
        hasResults: () => {
          return hasRetentionData(this._results);
        },

        dataTable: {
          closePopoverOnScroll: () => {
            this.closePopover();
          },

          handleSortChange: (ev: SortChangeEvent) => {
            propagateEvent(this, ev);
          },

          handleCellClick: (ev: CellClickEvent) => {
            const {field, context, target, bgColor, textColor} = ev.detail;
            const row = ev.detail.row as RetentionDataTableRow;
            const columnIdx = parseInt(field);
            if (isNaN(columnIdx) || columnIdx < 0 || columnIdx >= row.cells.length) {
              return;
            }

            const cell = row.cells[columnIdx];
            const popover = new RetentionPopover(
              {
                // bornEvent isn't populated for frequency type
                bornEventName: getEventDisplayName(this.state.query?.bornEvent || ``),
                bucketSizes: this.state.query.bucketSizes,
                dataGroupName: this.state.query.getDataGroupName(),
                dataGroupNamePlural: this.state.query.getDataGroupName({plural: true}),
                eventName: getEventDisplayName(this.state.query.event),
                groupBy: this.state.query.groupBy,
                retentionUnit: this.state.query.retentionUnit,
                dateUnit: this.state.query.dateUnit,
                dateRange: this.state.query.dateRange,
                retentionType: this.state.query.retentionType,
                unbounded: this.state.query.unbounded,
                projectTimezone: this.attr(`project-timezone`),
                query: this.state.query,
              },
              {
                absoluteValue: cell.formatted_value,
                absoluteDropOffValue: cell.absolute_drop_off_value,
                cohortSize: cell.cohort_size,
                columnIdx,
                rowIdx: context.rowIdx,
                groupedRowIdx: context.groupedRowIdx,
                completeDate: cell.complete_date,
                enteredValue: cell.entered_value,
                enteredDate: cell.date,
                incomplete: cell.incomplete,
                isAggregated: !row.isGroupedRow,
              },
            );
            this.update({
              popoverState: {
                retainedPercent: cell.formatted_percent,
                dropOffPercent: cell.percent_drop_off_value,
                bgColor,
                textColor,
                columnIdx,
                absoluteColumnIdx: context.columnIdx,
                rowIdx: context.rowIdx,
                groupedRowIdx: context.groupedRowIdx,
                popover,
                open: true,
                targetCell: target,
              },
            });
            trackPopover({
              eventName: Events.PopoverOpen,
              properties: {
                'is average row': popover.isAggregatedCell(),
              },
              component: this,
              query: this.state.query,
              popover,
            });
          },

          handleCollapseChange: (ev: CollapseChangeEvent) => {
            const {row, isCollapsed} = ev.detail;
            const expandedSegmentSet = new Set(this.state.query.getExpandedSegments());
            if (isCollapsed) {
              expandedSegmentSet.delete(row.identifier);
            } else {
              expandedSegmentSet.add(row.identifier);
            }
            this.dispatchEvent(
              new CustomEvent(`update`, {
                detail: {expandedSegments: Array.from(expandedSegmentSet)},
              }),
            );
          },
        },
      },
    };
  }

  updateSort(column: string) {
    if (this.state.query.sortColumn === column) {
      this.setSortDesc(!this.state.query.sortDesc);
    } else if (column === `name`) {
      this.setSortDesc(false);
      this.setSortColumn(`name`);
    } else {
      this.setSortDesc(true);
      this.setSortColumn(`cohort`);
    }
  }

  set rowData(rowData: RowWithAggregate[]) {
    this._results = rowData;
  }

  set query(query: RetentionQuery) {
    this.update({query});
  }

  setSortColumn(column: string) {
    this.dispatchEvent(new CustomEvent(`changeSortColumn`, {detail: {column}}));
  }

  setSortDesc(desc: boolean) {
    this.dispatchEvent(new CustomEvent(`changeSortDesc`, {detail: {desc}}));
  }

  getSelectedCell(): null | CellCoordinates {
    const {popoverState} = this.state;
    if (popoverState.open) {
      return {
        columnIdx: popoverState.absoluteColumnIdx,
        rowIdx: popoverState.rowIdx,
        groupedRowIdx: popoverState.groupedRowIdx,
      };
    }
    return null;
  }

  closePopover(): void {
    this.update({popoverState: {open: false}});
  }
}

export const RetentionTableEl = defineAndMakeJsxFactory<RetentionTableJSX>(`retention-table`, RetentionTable);
export const RetentionTableReact = panel2react<RetentionTableJSX>(`retention-table`, RetentionTable);
